import type { StatusType } from './constants';
import { evaluateFormula, type FormulaValue } from './formula';
import {
  isClosedType,
  isFilterGroup,
  type CustomFieldDTO,
  type FilterGroup,
  type FilterRule,
  type SortRule,
  type TaskDTO,
} from './tasks';

/** Lookups the filter, sort and group functions need. Built once per view render. */
export interface TaskQueryContext {
  now: number;
  /** The current user, for `me` values and Me mode. */
  userId: string;
  statuses: Map<string, { id: string; name: string; color: string; type: StatusType; position: string }>;
  fields: Map<string, CustomFieldDTO>;
  /** Display names for sorting and group labels. */
  users?: Map<string, { name: string }>;
  tags?: Map<string, { name: string; color: string }>;
  teams?: Map<string, { name: string; color: string }>;
  lists?: Map<string, { name: string }>;
}

const DAY = 86_400_000;

/** Start of the user's local day, expressed as the UTC-midnight convention used for date-only values. */
export function localDayUtc(now: number, offsetDays = 0): number {
  const d = new Date(now);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate() + offsetDays);
}

/**
 * Calendar day (as UTC midnight) a task date falls on, for date-only and timed values alike. A
 * date-only value is UTC midnight by convention, but one written through the API may carry a
 * time of day, so it is snapped to its UTC day rather than trusted.
 */
export function dayOf(ms: number, hasTime: boolean): number {
  if (!hasTime) {
    const d = new Date(ms);
    return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }
  const d = new Date(ms);
  return Date.UTC(d.getFullYear(), d.getMonth(), d.getDate());
}

/** Inclusive day range for a relative date preset. */
export function presetRange(preset: string, now: number): [number, number] | null {
  const today = localDayUtc(now);
  const weekday = new Date(now).getDay();
  const weekStart = today - weekday * DAY;
  switch (preset) {
    case 'today':
      return [today, today];
    case 'yesterday':
      return [today - DAY, today - DAY];
    case 'tomorrow':
      return [today + DAY, today + DAY];
    case 'overdue':
      return [-Infinity, today - DAY];
    case 'this_week':
      return [weekStart, weekStart + 6 * DAY];
    case 'next_week':
      return [weekStart + 7 * DAY, weekStart + 13 * DAY];
    case 'last_7_days':
      return [today - 7 * DAY, today];
    case 'next_7_days':
      return [today, today + 7 * DAY];
    case 'this_month': {
      const d = new Date(now);
      return [Date.UTC(d.getFullYear(), d.getMonth(), 1), Date.UTC(d.getFullYear(), d.getMonth() + 1, 0)];
    }
    default:
      return null;
  }
}

export function isOverdue(task: Pick<TaskDTO, 'dueDate' | 'dueHasTime' | 'completedAt'>, now: number): boolean {
  if (task.dueDate === null || task.completedAt !== null) return false;
  return task.dueHasTime ? task.dueDate < now : task.dueDate < localDayUtc(now);
}

type FieldKind = 'set' | 'scalar' | 'text' | 'number' | 'date' | 'bool';

interface FieldValue {
  kind: FieldKind;
  /** set: string[]; scalar: string | number | null; text: string; number: number | null; date: day ms | null; bool: boolean */
  value: unknown;
}

/** Reads a field from a task in a normalized form. Unknown fields read as an empty scalar. */
export function readField(task: TaskDTO, field: string, ctx: TaskQueryContext): FieldValue {
  switch (field) {
    case 'status':
      return { kind: 'scalar', value: task.statusId };
    case 'statusType':
      return { kind: 'scalar', value: ctx.statuses.get(task.statusId)?.type ?? null };
    case 'priority':
      return { kind: 'scalar', value: task.priority };
    case 'assignee':
      return { kind: 'set', value: task.assigneeIds };
    case 'team':
      return { kind: 'set', value: task.teamIds };
    case 'tag':
      return { kind: 'set', value: task.tagIds };
    case 'list':
      return { kind: 'set', value: [task.listId, ...task.extraListIds] };
    case 'createdBy':
      return { kind: 'scalar', value: task.createdBy };
    case 'name':
      return { kind: 'text', value: task.name };
    case 'customId':
      return { kind: 'text', value: task.customId ?? '' };
    case 'dueDate':
      return { kind: 'date', value: task.dueDate === null ? null : dayOf(task.dueDate, task.dueHasTime) };
    case 'startDate':
      return { kind: 'date', value: task.startDate === null ? null : dayOf(task.startDate, task.startHasTime) };
    case 'createdAt':
      return { kind: 'date', value: dayOf(task.createdAt, true) };
    case 'updatedAt':
      return { kind: 'date', value: dayOf(task.updatedAt, true) };
    case 'completedAt':
      return { kind: 'date', value: task.completedAt === null ? null : dayOf(task.completedAt, true) };
    case 'timeEstimate':
      return { kind: 'number', value: task.timeEstimateMs };
    case 'points':
      return { kind: 'number', value: task.points };
    case 'timeTracked':
      return { kind: 'number', value: task.timeTrackedMs };
    case 'archived':
      return { kind: 'bool', value: task.archived };
    case 'hasSubtasks':
      return { kind: 'bool', value: task.subtaskCount > 0 };
    case 'isSubtask':
      return { kind: 'bool', value: task.parentId !== null };
    default:
      break;
  }
  if (field.startsWith('cf:')) {
    const def = ctx.fields.get(field.slice(3));
    const raw = task.fields[field.slice(3)];
    if (!def) return { kind: 'scalar', value: null };
    switch (def.type) {
      case 'number':
      case 'currency':
      case 'rating':
      case 'progress':
        return { kind: 'number', value: typeof raw === 'number' ? raw : null };
      case 'date': {
        const v = raw as { ms?: number; hasTime?: boolean } | number | null | undefined;
        if (v === null || v === undefined) return { kind: 'date', value: null };
        return typeof v === 'number'
          ? { kind: 'date', value: v }
          : { kind: 'date', value: typeof v.ms === 'number' ? dayOf(v.ms, Boolean(v.hasTime)) : null };
      }
      case 'checkbox':
        return { kind: 'bool', value: raw === true };
      case 'labels':
      case 'user':
      case 'relationship':
        return { kind: 'set', value: Array.isArray(raw) ? raw : raw ? [raw] : [] };
      case 'dropdown':
        return { kind: 'scalar', value: (raw as string | null | undefined) ?? null };
      case 'formula': {
        const v = formulaValue(task, def, ctx);
        if (typeof v === 'number') return { kind: 'number', value: v };
        if (typeof v === 'boolean') return { kind: 'bool', value: v };
        return { kind: 'text', value: v === null ? '' : String(v) };
      }
      default:
        return { kind: 'text', value: typeof raw === 'string' ? raw : raw == null ? '' : String(raw) };
    }
  }
  return { kind: 'scalar', value: null };
}

function isEmpty(fv: FieldValue): boolean {
  if (fv.kind === 'set') return (fv.value as unknown[]).length === 0;
  if (fv.kind === 'text') return (fv.value as string).trim() === '';
  if (fv.kind === 'bool') return fv.value !== true;
  return fv.value === null || fv.value === undefined;
}

function resolveMe(value: unknown, ctx: TaskQueryContext): unknown {
  if (value === 'me') return ctx.userId;
  if (Array.isArray(value)) return value.map((v) => (v === 'me' ? ctx.userId : v));
  return value;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : value === undefined || value === null ? [] : [value];
}

export function matchesRule(task: TaskDTO, rule: FilterRule, ctx: TaskQueryContext): boolean {
  const fv = readField(task, rule.field, ctx);
  if (rule.op === 'is_set') return !isEmpty(fv);
  if (rule.op === 'is_not_set') return isEmpty(fv);
  const value = resolveMe(rule.value, ctx);

  if (fv.kind === 'date') {
    const day = fv.value as number | null;
    const range = typeof value === 'string' ? presetRange(value, ctx.now) : null;
    const target = typeof value === 'number' ? value : null;
    if (rule.op === 'is' || rule.op === 'is_not') {
      let hit = false;
      if (day !== null) {
        if (range) hit = day >= range[0] && day <= range[1];
        else if (target !== null) hit = day === dayOf(target, false);
      }
      // "overdue" only applies to unfinished work.
      if (hit && value === 'overdue' && rule.field === 'dueDate' && task.completedAt !== null) hit = false;
      return rule.op === 'is' ? hit : !hit;
    }
    if (day === null) return false;
    const lo = range ? range[0] : target;
    const hi = range ? range[1] : target;
    if (lo === null || hi === null) return false;
    if (rule.op === 'before' || rule.op === 'lt') return day < lo;
    if (rule.op === 'lte') return day <= hi;
    if (rule.op === 'after' || rule.op === 'gt') return day > hi;
    if (rule.op === 'gte') return day >= lo;
    return false;
  }

  if (fv.kind === 'set') {
    const have = fv.value as unknown[];
    const want = asArray(value);
    const any = want.some((w) => have.includes(w));
    if (rule.op === 'is' || rule.op === 'any_of' || rule.op === 'contains') return any;
    if (rule.op === 'is_not' || rule.op === 'none_of' || rule.op === 'not_contains') return !any;
    return false;
  }

  if (fv.kind === 'text') {
    const have = (fv.value as string).toLowerCase();
    const want = String(value ?? '').toLowerCase();
    if (rule.op === 'contains') return have.includes(want);
    if (rule.op === 'not_contains') return !have.includes(want);
    if (rule.op === 'is') return have === want;
    if (rule.op === 'is_not') return have !== want;
    return false;
  }

  if (fv.kind === 'bool') {
    const want = value === true || value === 'true';
    if (rule.op === 'is') return fv.value === want;
    if (rule.op === 'is_not') return fv.value !== want;
    return false;
  }

  if (fv.kind === 'number') {
    const have = fv.value as number | null;
    const want = typeof value === 'number' ? value : Number(value);
    if (rule.op === 'is') return have === want;
    if (rule.op === 'is_not') return have !== want;
    if (have === null || Number.isNaN(want)) return false;
    if (rule.op === 'gt') return have > want;
    if (rule.op === 'gte') return have >= want;
    if (rule.op === 'lt') return have < want;
    if (rule.op === 'lte') return have <= want;
    return false;
  }

  // scalar
  const want = asArray(value);
  const hit = want.includes(fv.value);
  if (rule.op === 'is' || rule.op === 'any_of') return hit;
  if (rule.op === 'is_not' || rule.op === 'none_of') return !hit;
  if (typeof fv.value === 'number' && typeof want[0] === 'number') {
    if (rule.op === 'gt') return fv.value > want[0];
    if (rule.op === 'gte') return fv.value >= want[0];
    if (rule.op === 'lt') return fv.value < want[0];
    if (rule.op === 'lte') return fv.value <= want[0];
  }
  return false;
}

/** Evaluates nested AND/OR groups. An empty group matches everything. */
export function matchesFilter(task: TaskDTO, filter: FilterGroup | undefined | null, ctx: TaskQueryContext): boolean {
  if (!filter || filter.rules.length === 0) return true;
  const test = (node: FilterRule | FilterGroup) =>
    isFilterGroup(node) ? matchesFilter(task, node, ctx) : matchesRule(task, node, ctx);
  return filter.op === 'or' ? filter.rules.some(test) : filter.rules.every(test);
}

export function countRules(filter: FilterGroup | undefined | null): number {
  if (!filter) return 0;
  return filter.rules.reduce((n, r) => n + (isFilterGroup(r) ? countRules(r) : 1), 0);
}

// ---------- Sorting ----------

function sortValue(task: TaskDTO, field: string, ctx: TaskQueryContext): string | number | null {
  if (field === 'status') return ctx.statuses.get(task.statusId)?.position ?? '';
  // Priority 0 means "none" and sorts last.
  if (field === 'priority') return task.priority === 0 ? null : task.priority;
  if (field === 'position') return task.position;
  if (field === 'assignee') {
    const first = task.assigneeIds[0];
    return first ? (ctx.users?.get(first)?.name.toLowerCase() ?? first) : null;
  }
  if (field === 'team') {
    const first = task.teamIds[0];
    return first ? (ctx.teams?.get(first)?.name.toLowerCase() ?? first) : null;
  }
  if (field === 'timeTracked') return task.timeTrackedMs || null;
  if (field === 'list') return ctx.lists?.get(task.listId)?.name.toLowerCase() ?? task.listId;
  if (field === 'tag') {
    const first = task.tagIds[0];
    return first ? (ctx.tags?.get(first)?.name.toLowerCase() ?? first) : null;
  }
  if (field === 'dueDate') return task.dueDate;
  if (field === 'startDate') return task.startDate;
  if (field === 'createdAt') return task.createdAt;
  if (field === 'updatedAt') return task.updatedAt;
  if (field === 'completedAt') return task.completedAt;
  const fv = readField(task, field, ctx);
  if (fv.kind === 'text') return (fv.value as string).toLowerCase() || null;
  if (fv.kind === 'bool') return fv.value ? 1 : 0;
  if (fv.kind === 'set') return ((fv.value as unknown[])[0] as string | undefined) ?? null;
  return (fv.value as string | number | null) ?? null;
}

/** Stable multi-key sort. Empty values always sort last; ties fall back to manual position. */
export function sortTasks(tasks: TaskDTO[], sort: SortRule[] | undefined | null, ctx: TaskQueryContext): TaskDTO[] {
  const rules = [...(sort ?? []), { field: 'position', dir: 'asc' as const }];
  return [...tasks].sort((a, b) => {
    for (const rule of rules) {
      const va = sortValue(a, rule.field, ctx);
      const vb = sortValue(b, rule.field, ctx);
      if (va === vb) continue;
      if (va === null) return 1;
      if (vb === null) return -1;
      const cmp = va < vb ? -1 : 1;
      return rule.dir === 'desc' ? -cmp : cmp;
    }
    return a.id < b.id ? -1 : 1;
  });
}

// ---------- Grouping ----------

export interface TaskGroup {
  /** Stable key, e.g. a status id, `p:2`, a user id, or `none`. */
  key: string;
  label: string;
  color: string | null;
  tasks: TaskDTO[];
}

const PRIORITY_LABELS = ['No priority', 'Urgent', 'High', 'Normal', 'Low'];
const PRIORITY_COLORS = ['#9aa0ad', '#e5484d', '#f5a524', '#3b82f6', '#8b8fa3'];
const NONE = 'none';

/**
 * Splits tasks into ordered groups. A task with several values (assignees, tags) appears in each.
 * `seed` lists groups that should exist even when empty (e.g. every status as a board column).
 */
export function groupTasks(
  tasks: TaskDTO[],
  groupBy: string | null | undefined,
  ctx: TaskQueryContext,
  seed: { key: string; label: string; color: string | null }[] = [],
): TaskGroup[] {
  if (!groupBy) return [{ key: 'all', label: 'All tasks', color: null, tasks }];
  const groups = new Map<string, TaskGroup>();
  for (const s of seed) groups.set(s.key, { ...s, tasks: [] });
  const add = (key: string, label: string, color: string | null, task: TaskDTO) => {
    let g = groups.get(key);
    if (!g) groups.set(key, (g = { key, label, color, tasks: [] }));
    g.tasks.push(task);
  };

  for (const task of tasks) {
    if (groupBy === 'status') {
      const s = ctx.statuses.get(task.statusId);
      // Group by name so lists with different status sets line up in multi-list views.
      add(s ? `status:${s.name.toLowerCase()}` : NONE, s?.name ?? 'No status', s?.color ?? null, task);
    } else if (groupBy === 'priority') {
      add(`p:${task.priority}`, PRIORITY_LABELS[task.priority] ?? 'No priority', PRIORITY_COLORS[task.priority] ?? null, task);
    } else if (groupBy === 'assignee') {
      if (task.assigneeIds.length === 0) add(NONE, 'Unassigned', null, task);
      for (const id of task.assigneeIds) add(id, ctx.users?.get(id)?.name ?? 'Unknown', null, task);
    } else if (groupBy === 'team') {
      if (task.teamIds.length === 0) add(NONE, 'No team', null, task);
      for (const id of task.teamIds) add(id, ctx.teams?.get(id)?.name ?? 'Unknown', ctx.teams?.get(id)?.color ?? null, task);
    } else if (groupBy === 'tag') {
      if (task.tagIds.length === 0) add(NONE, 'No tags', null, task);
      for (const id of task.tagIds) add(id, ctx.tags?.get(id)?.name ?? 'Unknown', ctx.tags?.get(id)?.color ?? null, task);
    } else if (groupBy === 'list') {
      add(task.listId, ctx.lists?.get(task.listId)?.name ?? 'Unknown list', null, task);
    } else if (groupBy === 'dueDate') {
      const today = localDayUtc(ctx.now);
      if (task.dueDate === null) add(NONE, 'No due date', null, task);
      else {
        const day = dayOf(task.dueDate, task.dueHasTime);
        if (day < today && task.completedAt === null) add('d:0', 'Overdue', '#e5484d', task);
        else if (day < today) add('d:1', 'Past', null, task);
        else if (day === today) add('d:2', 'Today', '#22c55e', task);
        else if (day === today + DAY) add('d:3', 'Tomorrow', '#3b82f6', task);
        else if (day <= today + 7 * DAY) add('d:4', 'Next 7 days', null, task);
        else add('d:5', 'Later', null, task);
      }
    } else if (groupBy.startsWith('cf:')) {
      const def = ctx.fields.get(groupBy.slice(3));
      const raw = task.fields[groupBy.slice(3)];
      const values = Array.isArray(raw) ? raw : raw === null || raw === undefined || raw === '' ? [] : [raw];
      if (values.length === 0) add(NONE, `No ${def?.name ?? 'value'}`, null, task);
      for (const v of values) {
        const option = def?.config.options?.find((o) => o.id === v);
        const label = option?.label ?? (def?.type === 'user' ? (ctx.users?.get(String(v))?.name ?? 'Unknown') : String(v));
        add(String(v), label, option?.color ?? null, task);
      }
    } else {
      add('all', 'All tasks', null, task);
    }
  }

  const order = (g: TaskGroup): string | number => {
    if (g.key === NONE) return groupBy === 'priority' ? 99 : '￿';
    if (groupBy === 'status') {
      const s = [...ctx.statuses.values()].find((x) => `status:${x.name.toLowerCase()}` === g.key);
      // Open first, then active, done, closed; ties by position.
      const rank = { open: 0, active: 1, done: 2, closed: 3 }[s?.type ?? 'open'];
      return `${rank}:${s?.position ?? ''}`;
    }
    if (groupBy === 'priority') return g.key === 'p:0' ? 98 : Number(g.key.slice(2));
    if (groupBy === 'dueDate') return g.key;
    if (groupBy.startsWith('cf:')) {
      const options = ctx.fields.get(groupBy.slice(3))?.config.options;
      const idx = options?.findIndex((o) => o.id === g.key) ?? -1;
      return idx >= 0 ? String(idx).padStart(4, '0') : g.label.toLowerCase();
    }
    return g.label.toLowerCase();
  };
  return [...groups.values()].sort((a, b) => {
    const oa = order(a);
    const ob = order(b);
    return oa < ob ? -1 : oa > ob ? 1 : 0;
  });
}

/** Applies a view's standing toggles (hide closed, Me mode) on top of its filter. */
export function applyView(
  tasks: TaskDTO[],
  config: { filter?: FilterGroup; hideClosed?: boolean; meMode?: boolean },
  ctx: TaskQueryContext,
): TaskDTO[] {
  return tasks.filter((task) => {
    if (config.hideClosed && isClosedType(ctx.statuses.get(task.statusId)?.type)) return false;
    if (config.meMode && !task.assigneeIds.includes(ctx.userId)) return false;
    return matchesFilter(task, config.filter, ctx);
  });
}

/**
 * Evaluates a formula field for a task. References in braces are resolved by field name, case
 * insensitive: built-ins (Name, Status, Priority, Due date, Start date, Time estimate, Points,
 * Time tracked, Created, Completed, Assignees, Subtasks, List) and the names of other custom fields.
 */
export function formulaValue(task: TaskDTO, def: CustomFieldDTO, ctx: TaskQueryContext, depth = 0): FormulaValue {
  const expression = def.config.formula;
  if (!expression || depth > 3) return null;
  const customByName = new Map<string, CustomFieldDTO>();
  for (const f of ctx.fields.values()) customByName.set(f.name.toLowerCase(), f);
  const get = (name: string): FormulaValue => {
    switch (name.toLowerCase()) {
      case 'name':
        return task.name;
      case 'status':
        return ctx.statuses.get(task.statusId)?.name ?? null;
      case 'priority':
        return task.priority;
      case 'due date':
      case 'due':
        return task.dueDate;
      case 'start date':
      case 'start':
        return task.startDate;
      case 'time estimate':
      case 'estimate':
        return task.timeEstimateMs;
      case 'points':
        return task.points;
      case 'time tracked':
      case 'tracked':
        return task.timeTrackedMs;
      case 'created':
        return task.createdAt;
      case 'completed':
        return task.completedAt;
      case 'assignees':
        return task.assigneeIds.length;
      case 'subtasks':
        return task.subtaskCount;
      case 'subtasks done':
        return task.subtaskDoneCount;
      case 'list':
        return ctx.lists?.get(task.listId)?.name ?? null;
    }
    const other = customByName.get(name.toLowerCase());
    if (!other || other.id === def.id) return null;
    const raw = task.fields[other.id];
    if (other.type === 'formula') return formulaValue(task, other, ctx, depth + 1);
    if (other.type === 'date') return typeof raw === 'object' && raw ? ((raw as { ms?: number }).ms ?? null) : typeof raw === 'number' ? raw : null;
    if (raw === undefined || raw === null) return null;
    if (Array.isArray(raw)) return raw.length;
    if (typeof raw === 'number' || typeof raw === 'string' || typeof raw === 'boolean') return raw;
    return String(raw);
  };
  return evaluateFormula(expression, { get, now: ctx.now }).value;
}
